import React from 'react';
import styled from '@emotion/styled';
import { spacing, zIndex, layout, bp } from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { NotificationBell } from './notification-bell';

/**
 * The notification bell, pinned to the TOP-RIGHT corner of every screen.
 *
 * WHY A DOCK AND NOT A HEADER SLOT
 * --------------------------------
 * The bell used to be mounted twice: once inside the staff rail and once inside
 * the resident dashboard's header. Two mounts meant two placements, two sets of
 * responsive rules, and a bell that a concierge and a resident found in
 * different corners of the product. It also meant every new shell had to
 * remember to add one, and the failure mode of forgetting was silent — no
 * error, just a person who never learns their parcel arrived.
 *
 * One fixed dock, mounted once in App.tsx beside the IQ launcher, removes all
 * of that. The two persistent surfaces now bookend the right edge: bell top,
 * IQ bottom, on every screen.
 *
 * `placement="header"` is the panel geometry that suits a top anchor — the list
 * drops BELOW the bell and right-aligns, so it stays on screen for a control
 * this close to the edge. Under md it becomes the same bottom sheet either
 * placement always collapsed to.
 */
const Dock = styled.div`
  position: fixed;
  /* Vertically CENTRED on the header it floats over, not pinned to the top
     gutter: the profile avatar sits in that header's own vertical centre, and
     a bell 6px higher than the avatar beside it reads as a misalignment rather
     than as a deliberate corner. (navHeightDesktop - touchTarget) / 2. */
  top: calc((${layout.navHeightDesktop} - ${layout.touchTarget}) / 2);
  right: ${spacing.md};

  /* A FIXED DOCK DOES NOT MOVE WHEN THE DOCUMENT DOES.
     The PWA install banner now pushes the page down while it is up, but this
     is positioned against the VIEWPORT, so it stayed under the banner and the
     bell went on resolving to it — measured by scripts/analytics/responsive.js
     as "button covered by div{region}" on every touch shape. The class is set
     on the root element by components/install-prompt/install-prompt.tsx. */
  html.has-install-banner & {
    top: calc(
      52px + env(safe-area-inset-top) +
        (${layout.navHeightDesktop} - ${layout.touchTarget}) / 2
    );
  }
  /* The furniture tier — the same layer as the rail and the IQ launcher. Above
     page content, below every dialog: a bell floating over an open modal would
     be a way to navigate out of one. */
  z-index: ${zIndex.sticky};

  /* The dock is only as big as the bell; without this it would be an invisible
     full-width strip swallowing clicks along the top edge. The button itself
     takes pointer events back. */
  pointer-events: none;

  > * {
    pointer-events: auto;
    /* Never let the control fall under a thumb's minimum, whatever the bell's
       own styling does. */
    min-width: ${layout.touchTarget};
    min-height: ${layout.touchTarget};
  }

  ${down('md')} {
    /* Notched phones in portrait: the status bar overlays the top edge. */
    top: calc(${spacing.sm} + env(safe-area-inset-top));
    right: ${spacing.sm};
  }

  ${down('sm')} {
    top: calc(${spacing.xs} + env(safe-area-inset-top));
    right: ${spacing.xs};
  }

  /* Short landscape windows on a phone — the top edge is scarce, so sit tight
     to the corner. Width-bounded too: a short but wide window is a laptop with
     a squashed browser, which has room to spare. */
  @media (max-width: ${bp.md - 0.02}px) and (max-height: 480px) {
    top: calc(${spacing.xs} + env(safe-area-inset-top));
  }
`;

export const NotificationDock: React.FC = () => (
  <Dock>
    <NotificationBell placement="header" />
  </Dock>
);
